import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import ReferenceDocumentsTabTable from './ReferenceDocumentsTabTable';

const TABS = [
  {
    value: 'complaints',
    label: 'Complaints',
    endpoint: '/complaint',
    getDate: (row) => row.reportedAt || row.createdAt,
    getDescription: (row) => row.description || row.title,
  },
  {
    value: 'deviations',
    label: 'Deviations',
    endpoint: '/deviation',
    getDate: (row) => row.reportedAt || row.discoveredAt || row.createdAt,
    getDescription: (row) => row.description || row.title,
  },
  {
    value: 'risk-assessments',
    label: 'Risk Assessments',
    endpoint: '/risk-assessment',
    getDate: (row) => row.requestedDate || row.receivedDate || row.createdAt,
    getDescription: (row) => row.parameter || row.title || row.effect,
  },
];

/**
 * Settings → Reference Documents
 * Lists Complaints / Deviations / Risk Assessments; inline edit only on referenceDocument.
 */
const ReferenceDocumentsPage = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('complaints');

  return (
    <div className="h-full min-h-0 flex flex-col p-4 md:p-6 gap-4">
      <div>
        <h1 className={cn('text-xl font-semibold', theme.text.primary)}>
          Reference Documents
        </h1>
        <p className={cn('text-sm mt-1', theme.text.secondary)}>
          View records and update the reference document snapshot. Other fields
          are read-only.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex-1 min-h-0 flex flex-col"
      >
        <div
          role="tablist"
          className={cn(
            'inline-flex w-fit max-w-full flex-wrap gap-1 rounded-xl border p-1.5',
            theme.border.light,
            'bg-slate-100/90 dark:bg-slate-900/60',
          )}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                  isActive
                    ? 'bg-blue-600 text-white shadow-md ring-1 ring-blue-600/30'
                    : cn(
                        theme.text.secondary,
                        'hover:bg-white hover:text-slate-900',
                        'dark:hover:bg-slate-800 dark:hover:text-white',
                      ),
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {TABS.map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className="mt-4 flex-1 min-h-0 flex flex-col"
          >
            {activeTab === tab.value && (
              <ReferenceDocumentsTabTable
                endpoint={tab.endpoint}
                getDate={tab.getDate}
                getDescription={tab.getDescription}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ReferenceDocumentsPage;
